import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import Reveal from '@/components/landing/reveal'
import MemoryPhoto from '@/components/landing/memory-photo'
import SectionHeading from '@/components/landing/section-heading'
import { STORY } from '@/constants/birthday'

const StorySection: FC = (): ReactNode => {
    return (
        <section id='story' aria-labelledby='story-title' className='relative overflow-hidden py-24 sm:py-32'>
            <div className='mx-auto max-w-6xl px-5 sm:px-8'>
                <Reveal>
                    <SectionHeading
                        id='story-title'
                        eyebrow={STORY.eyebrow}
                        title={STORY.title}
                        subtitle={STORY.subtitle}
                    />
                </Reveal>

                <div className='relative mt-16 sm:mt-20'>
                    <span aria-hidden className='absolute bottom-0 left-5 top-0 w-px bg-gradient-to-b from-transparent via-primary/40 to-transparent md:left-1/2' />

                    <ol className='space-y-16 md:space-y-24'>
                        {STORY.chapters.map((chapter, index) => {
                            const flipped = index % 2 === 1
                            const landscape = chapter.photo.rotate

                            return (
                                <li
                                    key={chapter.title}
                                    className='relative grid items-center gap-6 pl-14 md:grid-cols-2 md:gap-20 md:pl-0'
                                >
                                    <span
                                        aria-hidden
                                        className='absolute left-5 top-0 z-10 grid size-10 -translate-x-1/2 place-items-center rounded-full bg-primary font-heading text-lg font-semibold text-primary-foreground shadow-lg shadow-primary/30 ring-4 ring-background md:left-1/2 md:top-1/2 md:-translate-y-1/2'
                                    >
                                        {index + 1}
                                    </span>

                                    <Reveal className={cn(flipped && 'md:order-2')}>
                                        <div
                                            className={cn(
                                                'w-full rounded-[1.75rem] bg-white p-2.5 shadow-xl shadow-primary/10',
                                                landscape ? 'max-w-md' : 'max-w-[20rem]',
                                                flipped ? 'md:mr-auto md:rotate-2' : 'md:ml-auto md:-rotate-2'
                                            )}
                                        >
                                            <MemoryPhoto
                                                photo={chapter.photo}
                                                ratio={landscape ? 4 / 3 : 4 / 5}
                                                className='rounded-[1.35rem]'
                                            />
                                        </div>
                                    </Reveal>

                                    <Reveal delay={120} className={cn(flipped && 'md:order-1 md:text-right')}>
                                        <p className='text-xs font-extrabold uppercase tracking-[0.25em] text-primary'>
                                            {STORY.chapterLabel} {index + 1}
                                        </p>

                                        <h3 className='mt-3 font-heading text-2xl font-semibold text-balance sm:text-3xl'>
                                            {chapter.title}
                                        </h3>

                                        <p className='mt-3 text-base leading-relaxed text-muted-foreground text-pretty sm:text-lg'>
                                            {chapter.text}
                                        </p>
                                    </Reveal>
                                </li>
                            )
                        })}
                    </ol>
                </div>
            </div>
        </section>
    )
}

export default StorySection
